import { Injectable, signal, computed } from '@angular/core';
import { FirmaService } from '../../firma-yonetim/services/firma.service';
import type { IOfflineKayit, GirisCikisTur } from '../../firma-yonetim/interfaces/pdks.interface';

const DB_NAME = 'fikoyt_pdks_offline';
const DB_VERSION = 1;
const STORE_NAME = 'kayitlar';
const LS_KEY = 'fikoyt_pdks_offline_kayitlar';

@Injectable({ providedIn: 'root' })
export class PdksOfflineService {
  bekleyenSayisi = signal(0);
  senkronizeEdiliyor = signal(false);
  sonHata = signal<string | null>(null);

  private db: IDBDatabase | null = null;
  private indexedDBDestekli = false;

  constructor() {
    this.indexedDBDestekli = typeof indexedDB !== 'undefined';
    this.baslat();
  }

  private async baslat() {
    if (this.indexedDBDestekli) {
      try {
        this.db = await this.dbAc();
      } catch {
        this.indexedDBDestekli = false;
      }
    }
    await this.bekleyenGuncelle();

    // Online olunca otomatik senkronize
    window.addEventListener('online', () => this.otomatikSenkronize());
  }

  // ─── Kayıt Ekle ──────────────────────────────────────────────────────────

  async kayitEkle(
    firmaId: string,
    personelId: string,
    kartUid: string,
    cihazId: string,
    tur: GirisCikisTur,
  ): Promise<void> {
    const kayit: IOfflineKayit = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      firmaId, personelId, kartUid, cihazId, tur,
      zaman: new Date().toISOString(),
      senkronize: false,
      denemeSayisi: 0,
    };

    if (this.indexedDBDestekli && this.db) {
      await this.idbEkle(kayit);
    } else {
      this.lsEkle(kayit);
    }
    await this.bekleyenGuncelle();
  }

  // ─── Senkronize Et ────────────────────────────────────────────────────────

  async senkronizeEt(firmaService: FirmaService): Promise<{ basarili: number; basarisiz: number }> {
    if (this.senkronizeEdiliyor() || !navigator.onLine) return { basarili: 0, basarisiz: 0 };
    this.senkronizeEdiliyor.set(true);
    this.sonHata.set(null);

    let basarili = 0, basarisiz = 0;
    try {
      const kayitlar = await this.tumKayitlarAl();
      const bekleyenler = kayitlar.filter(k => !k.senkronize && k.denemeSayisi < 5);

      for (const k of bekleyenler) {
        try {
          await firmaService.girisCikisKaydet(k.firmaId, {
            personelId: k.personelId,
            kartUid: k.kartUid,
            cihazId: k.cihazId,
            tur: k.tur,
            zaman: k.zaman,
            onayDurumu: 'otomatik',
            istemciKayitId: k.id,
          } as any);
          k.senkronize = true;
          basarili++;
        } catch (e: any) {
          k.denemeSayisi++;
          k.hataKodu = e?.message || 'bilinmeyen_hata';
          basarisiz++;
        }
        await this.kayitGuncelle(k);
      }

      // Başarılı olanları temizle
      if (basarili > 0) await this.senkronizeEdilenleriTemizle();
    } catch (e: any) {
      this.sonHata.set(e?.message || 'Senkronizasyon hatası');
    } finally {
      this.senkronizeEdiliyor.set(false);
      await this.bekleyenGuncelle();
    }

    return { basarili, basarisiz };
  }

  // ─── IndexedDB ────────────────────────────────────────────────────────────

  private dbAc(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('senkronize', 'senkronize', { unique: false });
          store.createIndex('firmaId', 'firmaId', { unique: false });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  private idbEkle(kayit: IOfflineKayit): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).add(kayit);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  private async tumKayitlarAl(): Promise<IOfflineKayit[]> {
    if (this.indexedDBDestekli && this.db) {
      return new Promise((resolve, reject) => {
        const tx = this.db!.transaction(STORE_NAME, 'readonly');
        const req = tx.objectStore(STORE_NAME).getAll();
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => reject(req.error);
      });
    }
    return this.lsAl();
  }

  private async kayitGuncelle(kayit: IOfflineKayit): Promise<void> {
    if (this.indexedDBDestekli && this.db) {
      return new Promise((resolve, reject) => {
        const tx = this.db!.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).put(kayit);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    }
    const kayitlar = this.lsAl();
    const idx = kayitlar.findIndex(k => k.id === kayit.id);
    if (idx >= 0) { kayitlar[idx] = kayit; this.lsKaydet(kayitlar); }
  }

  private async senkronizeEdilenleriTemizle(): Promise<void> {
    if (this.indexedDBDestekli && this.db) {
      return new Promise((resolve, reject) => {
        const tx = this.db!.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const idx = store.index('senkronize');
        const req = idx.openCursor(IDBKeyRange.only(true));
        req.onsuccess = () => {
          const cursor = req.result;
          if (cursor) { cursor.delete(); cursor.continue(); }
        };
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    }
    const kayitlar = this.lsAl().filter(k => !k.senkronize);
    this.lsKaydet(kayitlar);
  }

  private async bekleyenGuncelle(): Promise<void> {
    const kayitlar = await this.tumKayitlarAl();
    this.bekleyenSayisi.set(kayitlar.filter(k => !k.senkronize).length);
  }

  private async otomatikSenkronize() {
    // 2 sn bekle, stabil bağlantı kontrolü
    await new Promise(r => setTimeout(r, 2000));
    if (navigator.onLine && this.bekleyenSayisi() > 0) {
      // FirmaService inject edemiyoruz (circular), callback ile çağrılacak
      // Bu servis dışarıdan senkronizeEt() çağrısı bekler
    }
  }

  // ─── localStorage Fallback ────────────────────────────────────────────────

  private lsEkle(kayit: IOfflineKayit) {
    const kayitlar = this.lsAl();
    kayitlar.push(kayit);
    this.lsKaydet(kayitlar);
  }

  private lsAl(): IOfflineKayit[] {
    try {
      return JSON.parse(localStorage.getItem(LS_KEY) || '[]');
    } catch { return []; }
  }

  private lsKaydet(kayitlar: IOfflineKayit[]) {
    localStorage.setItem(LS_KEY, JSON.stringify(kayitlar));
  }
}
